export const parseDateOnly = (dateString) => {
  if (!dateString) return null;

  if (dateString instanceof Date) {
    return {
      year: dateString.getUTCFullYear(),
      month: dateString.getUTCMonth(),
      day: dateString.getUTCDate()
    };
  }

  const dateStr = typeof dateString === 'string' ? dateString.split('T')[0] : String(dateString);
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return {
      year: parseInt(parts[0], 10),
      month: parseInt(parts[1], 10) - 1,
      day: parseInt(parts[2], 10)
    };
  }

  const d = new Date(dateString);
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth(),
    day: d.getUTCDate()
  };
};

export const getCourseTimelineStatus = (course) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startDateParts = parseDateOnly(course.start_date);
  const endDateParts = parseDateOnly(course.end_date);

  const courseStartDate = startDateParts
    ? new Date(startDateParts.year, startDateParts.month, startDateParts.day)
    : null;
  const courseEndDate = endDateParts
    ? new Date(endDateParts.year, endDateParts.month, endDateParts.day)
    : null;

  return {
    hasStarted: !courseStartDate || courseStartDate <= today,
    hasEnded: Boolean(courseEndDate && courseEndDate < today)
  };
};

export const formatCourseDate = (course) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (!course.start_date) {
    return 'TBD';
  }

  const startDateParts = parseDateOnly(course.start_date);
  const courseStartDate = new Date(startDateParts.year, startDateParts.month, startDateParts.day);

  // Format date using the parsed parts directly
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const formattedStartDate = `${monthNames[startDateParts.month]} ${startDateParts.day}${startDateParts.year !== now.getFullYear() ? `, ${startDateParts.year}` : ''}`;

  // Check if course has ended
  let hasEnded = false;
  let formattedEndDate = '';

  if (course.end_date) {
    const endDateParts = parseDateOnly(course.end_date);
    const courseEndDate = new Date(endDateParts.year, endDateParts.month, endDateParts.day);
    hasEnded = courseEndDate < today;
    formattedEndDate = `${monthNames[endDateParts.month]} ${endDateParts.day}${endDateParts.year !== now.getFullYear() ? `, ${endDateParts.year}` : ''}`;
  }

  // Determine course status
  const hasStarted = courseStartDate <= today;

  if (hasEnded) {
    // Course has ended
    return `Started: ${formattedStartDate} - Ended: ${formattedEndDate}`;
  } else if (hasStarted) {
    // Course has started but not ended
    return `Started: ${formattedStartDate} - Ends: ${formattedEndDate}`;
  } else {
    // Course has not started yet
    return `Starts: ${formattedStartDate} - Ends: ${formattedEndDate}`;
  }
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const toLocalDate = (value) => {
  const parts = parseDateOnly(value);
  return parts ? new Date(parts.year, parts.month, parts.day) : null;
};

/**
 * Derives timeline progress from the course's start/end dates.
 * Returns null when either date is missing or the course hasn't started yet.
 * Shape: { weekNumber, totalWeeks, percent, hasEnded }
 */
export const getCourseProgress = (course) => {
  const start = toLocalDate(course.start_date);
  const end = toLocalDate(course.end_date);
  if (!start || !end || end <= start) return null;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (today < start) return null;

  const totalDays = Math.round((end - start) / MS_PER_DAY);
  const elapsedDays = Math.round((today - start) / MS_PER_DAY);
  // Classes meet weekly on both the start and end dates (e.g. Oct 21 – Dec 9 is
  // 8 sessions), so the end date opens a new week rather than closing the last.
  const totalWeeks = Math.max(1, Math.floor(totalDays / 7) + 1);
  const weekNumber = Math.min(totalWeeks, Math.floor(elapsedDays / 7) + 1);
  const percent = Math.max(0, Math.min(100, Math.round((elapsedDays / totalDays) * 100)));

  return { weekNumber, totalWeeks, percent, hasEnded: end < today };
};

export const getInstructorInitials = (course) => {
  const first = (course.instructor_first_name || '').trim().charAt(0);
  const last = (course.instructor_last_name || '').trim().charAt(0);
  return `${first}${last}`.toUpperCase();
};

/** Compact "Oct 22 – Dec 10" range for card display (year added when not current). */
export const formatCourseDateRange = (course) => {
  if (!course.start_date) return 'TBD';
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const currentYear = new Date().getFullYear();
  const fmt = (value) => {
    const p = parseDateOnly(value);
    return `${monthNames[p.month]} ${p.day}${p.year !== currentYear ? `, ${p.year}` : ''}`;
  };
  return course.end_date ? `${fmt(course.start_date)} – ${fmt(course.end_date)}` : fmt(course.start_date);
};

const LETTER_CUTOFFS = [[97, 'A+'], [93, 'A'], [90, 'A-'], [87, 'B+'], [83, 'B'], [80, 'B-'], [77, 'C+'], [73, 'C'], [70, 'C-'], [67, 'D+'], [63, 'D'], [60, 'D-']];

/** The final grade to show on a course card ("A-", "Pass"), or '' when none has been entered. */
export const finalGradeLabel = (entry) => {
  const raw = entry && entry.grade != null ? String(entry.grade).trim() : '';
  if (!raw) return '';
  if (Number.isNaN(Number(raw))) return raw;
  const score = Number(entry.numericGrade != null ? entry.numericGrade : raw);
  const hit = LETTER_CUTOFFS.find(([min]) => score >= min);
  return hit ? hit[1] : 'F';
};
